/**
 * Cohesive ChatKit — message presentation: UserMsg, AgentProse, Feedback.
 *
 * Ported from cohesive-design-system/showcase/lib/chat-kit.jsx. Departures:
 * - UserMsg renders `children` (real entity mentions come from the product
 *   message pipeline), never the demo regex highlighter.
 * - AgentProse is a typography contract wrapping already-rendered markdown
 *   (no dangerouslySetInnerHTML).
 * - Feedback renders only the actions it is given handlers for; thumbs and
 *   regenerate stay hidden until their backends exist.
 */
import { Check, Copy, RotateCcw, ThumbsDown, ThumbsUp } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { AttachmentChip, type ChatAttachment } from "./atoms";

/** User message: right-aligned bubble with optional attachment chips above. */
export function UserMsg({
  children,
  attachments = [],
  time,
  footer,
  cancelled,
  className,
}: {
  children: React.ReactNode;
  attachments?: ChatAttachment[];
  /** Pre-formatted timestamp label (e.g. "9:24am", "now"). */
  time?: string;
  /** Slot below the bubble (e.g. referenced-entity pills). */
  footer?: React.ReactNode;
  cancelled?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex max-w-[84%] flex-col items-end gap-1.5 self-end",
        cancelled && "opacity-50",
        className,
      )}
    >
      {attachments.length > 0 && (
        <div className="flex flex-wrap justify-end gap-1.5">
          {attachments.map((a, i) => (
            // ChatAttachment has no id; disambiguate same-named files by index.
            <AttachmentChip key={`${a.name}-${i}`} attachment={a} />
          ))}
        </div>
      )}
      <div className="rounded-lg rounded-br-xs bg-n-10 px-4 py-3 font-sans text-md text-n-90 text-pretty [&_.chat-mention]:font-medium [&_.chat-mention]:text-rust">
        {children}
      </div>
      {time && <span className="font-sans text-xs text-n-50">{time}</span>}
      {footer}
    </div>
  );
}

/**
 * Agent prose typography — plain on the surface (no bubble), pinned to the
 * reading measure. Use the class bundle directly when wrapping an existing
 * markdown renderer.
 */
export const agentProseClassName = cn(
  "max-w-measure font-sans text-md text-n-80 text-pretty",
  "[&_strong]:font-semibold [&_strong]:text-n-90 [&_em]:font-serif [&_em]:italic",
  "[&_code]:rounded-xs [&_code]:bg-n-10 [&_code]:px-1.5 [&_code]:py-px [&_code]:font-mono [&_code]:text-[0.85em] [&_code]:text-n-90",
  // Lists are styled by the Markdown renderer (list-disc/decimal + indent). Do
  // NOT set `display:flex` on `ul` here — it blockifies the `li`s and suppresses
  // their list markers, which dropped the bullets in chat.
  "[&_p:not(:last-child)]:mb-3",
  "[&_sup]:ml-px [&_sup]:align-super [&_sup]:text-[0.62em] [&_sup]:font-semibold [&_sup]:text-rust",
);

export function AgentProse({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn(agentProseClassName, className)}>{children}</div>;
}

/**
 * Post-answer action row. Copy is always available when `copyText` is given;
 * vote and regenerate appear only when their handlers exist. `children` is
 * the trailing slot (e.g. a more-menu).
 */
export function Feedback({
  copyText,
  onCopied,
  vote,
  onVote,
  onRegenerate,
  children,
  className,
}: {
  copyText?: string;
  /** Fires after a successful copy (toasts, analytics). */
  onCopied?: () => void;
  vote?: "up" | "down" | null;
  onVote?: (vote: "up" | "down" | null) => void;
  onRegenerate?: () => void;
  children?: React.ReactNode;
  className?: string;
}) {
  const [copied, setCopied] = React.useState(false);
  const copyTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  React.useEffect(() => () => clearTimeout(copyTimer.current), []);

  const handleCopy = async () => {
    if (!copyText) return;
    await navigator.clipboard.writeText(copyText);
    onCopied?.();
    setCopied(true);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className={cn("mt-1 flex items-center gap-0.5", className)}>
      {onVote && (
        <>
          <Button
            variant="ghost"
            size="icon-sm"
            title="Good response"
            className={cn(vote === "up" && "text-rust")}
            onClick={() => onVote(vote === "up" ? null : "up")}
          >
            <ThumbsUp />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            title="Bad response"
            className={cn(vote === "down" && "text-rust")}
            onClick={() => onVote(vote === "down" ? null : "down")}
          >
            <ThumbsDown />
          </Button>
        </>
      )}
      {copyText !== undefined && (
        <Button
          variant="ghost"
          size="icon-sm"
          title="Copy"
          onClick={handleCopy}
        >
          {copied ? <Check /> : <Copy />}
        </Button>
      )}
      {onRegenerate && (
        <Button
          variant="ghost"
          size="icon-sm"
          title="Regenerate"
          onClick={onRegenerate}
        >
          <RotateCcw />
        </Button>
      )}
      {children}
    </div>
  );
}
